import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Home as HomeIcon, ContactMail as ContactIcon } from '@mui/icons-material';

// Import components
import ContactForm from '../components/ContactForm';
import ContactInfo from '../components/ContactInfo';
import MapEmbed from '../components/MapEmbed';
import SocialLinks from '../components/SocialLinks';

// Import styles
import './Contact.css';

const Contact = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact - Mission Sport",
    "description": "Contactez Mission Sport pour toute question ou collaboration. Nous sommes à votre écoute pour vous accompagner dans vos projets sportifs.",
    "url": "https://missionsport.com/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Mission Sport",
      "email": "contact@missionsport.com",
      "telephone": "+33 1 23 45 67 89",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "123 Avenue des Sports",
        "addressLocality": "Paris",
        "postalCode": "75001",
        "addressCountry": "FR"
      }
    }
  };

  return (
    <HelmetProvider>
      <Box className="contact-page">
        <Helmet>
          <title>Contact - Mission Sport | Nous sommes à votre écoute</title>
          <meta 
            name="description" 
            content="Contactez Mission Sport pour toute question ou collaboration. Formulaire de contact, coordonnées directes et carte interactive. Réponse sous 24h garantie."
          />
          <meta 
            name="keywords" 
            content="contact mission sport, formulaire contact, coordonnées mission sport, collaboration sportive, questions sport"
          />
          
          {/* Open Graph */}
          <meta property="og:title" content="Contact - Mission Sport | Nous sommes à votre écoute" />
          <meta 
            property="og:description" 
            content="Contactez Mission Sport pour toute question ou collaboration. Formulaire de contact, coordonnées directes et carte interactive."
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://missionsport.com/contact" />
          <meta property="og:image" content="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630" />
          <meta property="og:site_name" content="Mission Sport" />
          <meta property="og:locale" content="fr_FR" />
          
          {/* Twitter Cards */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Contact - Mission Sport | Nous sommes à votre écoute" />
          <meta 
            name="twitter:description" 
            content="Contactez Mission Sport pour toute question ou collaboration. Formulaire de contact, coordonnées directes et carte interactive."
          />
          <meta name="twitter:image" content="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630" />
          <meta name="twitter:site" content="@missionsport" />
          
          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
          <meta name="author" content="Mission Sport" />
          <link rel="canonical" href="https://missionsport.com/contact" />
          
          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>

        {/* Hero Section */}
        <Box className="contact-hero">
          <Container maxWidth="lg">
            <Breadcrumbs 
              aria-label="breadcrumb" 
              className="contact-breadcrumbs"
              sx={{ mb: 2 }}
            >
              <Link 
                component={RouterLink} 
                to="/" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center' }}
                aria-label="Retour à l'accueil"
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Accueil
              </Link>
              <Typography 
                color="text.primary" 
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ContactIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Contact
              </Typography>
            </Breadcrumbs>
            
            <Typography 
              variant="h1" 
              component="h1" 
              className="contact-title"
              gutterBottom
            >
              Contactez-nous
            </Typography>
            
            <Typography 
              variant="h2" 
              component="p" 
              className="contact-subtitle"
            >
              Nous sommes à votre écoute pour toute question ou collaboration
            </Typography>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg" className="contact-content">
          <Grid container spacing={4}>
            {/* Left Column - Contact Form */}
            <Grid item xs={12} lg={6}>
              <Box component="section" aria-labelledby="contact-form-title">
                <ContactForm />
              </Box>
            </Grid>

            {/* Right Column - Contact Info & Map */}
            <Grid item xs={12} lg={6}>
              <Box className="contact-sidebar">
                <Box component="section" aria-labelledby="contact-info-title" sx={{ mb: 4 }}>
                  <ContactInfo />
                </Box>
                
                <Box component="section" aria-labelledby="map-title" sx={{ mb: 4 }}>
                  <MapEmbed />
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Social Links Section */}
          <Box component="section" aria-labelledby="social-links-title" sx={{ mt: 6 }}>
            <SocialLinks />
          </Box>

          {/* Additional Information */}
          <Box component="section" className="contact-additional-info" sx={{ mt: 6 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box className="info-card">
                  <Typography variant="h5" component="h3" gutterBottom>
                    🕒 Horaires d'ouverture
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Lundi - Vendredi :</strong> 9h00 - 18h00<br />
                    <strong>Samedi :</strong> 10h00 - 16h00<br />
                    <strong>Dimanche :</strong> Fermé
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Support téléphonique disponible pendant les heures d'ouverture
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box className="info-card">
                  <Typography variant="h5" component="h3" gutterBottom>
                    📧 Types de demandes
                  </Typography>
                  <Typography variant="body1" paragraph>
                    • Questions générales<br />
                    • Partenariats et collaborations<br />
                    • Support technique<br />
                    • Demandes de presse<br />
                    • Opportunités de carrière
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nous répondons à toutes les demandes sous 24h ouvrées
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </HelmetProvider>
  );
};

export default Contact;